import { supabase, isSupabaseConfigured } from './supabase.js';
import { isAuthenticated, getUser } from './auth.js';
import { state, keyframeToText } from '../editor/state.js';

// ================================================================
// Per-sequence + per-keyframe cloud sync
// ================================================================
// Rules:
//  - Signed-in users auto-upsert their entire keyframe library on each
//    debounced tick (diffed by JSON snapshot).
//  - A sequence only uploads when the user explicitly clicks Save;
//    afterwards it auto-updates on debounce if content changes.
//  - `_`-prefixed fields on sequences/keyframes are local-only sync
//    metadata; they're stripped before hitting Supabase.

const AUTOSAVE_DELAY = 1500;

let autosaveTimer = null;

// Snapshots of what's currently on the server, used to skip no-op updates.
const lastSavedKeyframeJson = new Map(); // kf.id -> JSON string
const lastSavedSequenceJson = new Map(); // cloudId -> JSON string

// Global status indicator (topbar)
let saveStatus = 'idle'; // idle | saving | saved | error
const statusListeners = new Set();

export function getSaveStatus() { return saveStatus; }
export function onSaveStatusChange(fn) {
  statusListeners.add(fn);
  return () => statusListeners.delete(fn);
}
function setStatus(s) {
  saveStatus = s;
  for (const fn of statusListeners) fn(s);
}

// Re-render hook — the editor registers a callback so that cloud changes
// (e.g. hydration) trigger a UI refresh without importing UI modules here.
let libraryChangedCb = () => {};
export function setLibraryChangedCallback(fn) { libraryChangedCb = fn; }

// ----------------------------------------------------------------
// Payload helpers
// ----------------------------------------------------------------
function stripUnderscoreFields(obj) {
  const out = {};
  for (const k of Object.keys(obj)) {
    if (!k.startsWith('_')) out[k] = obj[k];
  }
  return out;
}

function extractThumbnailFromSequence(seq) {
  const firstStep = seq?.steps?.[0];
  const kfId = firstStep?.keyframeId;
  const kf = kfId && state.project.keyframes[kfId];
  if (!kf) return { text: '', color: '#ca4b15', rows: 3, cols: 9 };
  return {
    text: keyframeToText(kf),
    color: kf.defaultColor || '#ca4b15',
    rows: kf.rows,
    cols: kf.cols,
  };
}

function keyframePayload(kf) {
  return {
    id: kf.id,
    user_id: getUser().id,
    name: kf.name,
    rows: kf.rows,
    cols: kf.cols,
    grid: kf.grid,
    default_color: kf.defaultColor || '#ca4b15',
  };
}

function sequencePayload(seq, title) {
  const cleanSeq = stripUnderscoreFields(seq);
  const thumb = extractThumbnailFromSequence(seq);
  const kfIds = Array.from(
    new Set((seq.steps || []).map(s => s.keyframeId).filter(Boolean))
  );
  return {
    title: title || seq.name || 'Untitled',
    sequence_data: {
      ...cleanSeq,
      gridSize: state.project.gridSize,
      backgroundColor: state.project.backgroundColor,
      fontSize: state.project.fontSize,
    },
    keyframe_ids: kfIds,
    thumbnail_text: thumb.text,
    thumbnail_color: thumb.color,
    thumbnail_rows: thumb.rows,
    thumbnail_cols: thumb.cols,
  };
}

// ----------------------------------------------------------------
// Keyframe upsert (diffed)
// ----------------------------------------------------------------
async function upsertKeyframes(ids) {
  if (!isAuthenticated() || !isSupabaseConfigured()) return;
  const rows = [];
  for (const id of ids) {
    const kf = state.project.keyframes[id];
    if (!kf) continue;
    const payload = keyframePayload(kf);
    const snap = JSON.stringify(payload);
    if (lastSavedKeyframeJson.get(id) === snap) continue;
    rows.push(payload);
    lastSavedKeyframeJson.set(id, snap);
  }
  if (rows.length === 0) return;
  const { error } = await supabase
    .from('keyframes')
    .upsert(rows, { onConflict: 'id' });
  if (error) {
    for (const r of rows) lastSavedKeyframeJson.delete(r.id);
    throw error;
  }
  const now = Date.now();
  for (const r of rows) {
    const kf = state.project.keyframes[r.id];
    if (kf) kf._syncedAt = now;
  }
}

// ----------------------------------------------------------------
// Per-sequence: explicit save
// ----------------------------------------------------------------
export async function saveSequenceToCloud(idx) {
  if (!isAuthenticated()) throw new Error('Sign in to save to cloud');
  if (!isSupabaseConfigured()) throw new Error('Cloud storage not configured');
  const seq = state.project.sequences[idx];
  if (!seq) return;

  setStatus('saving');
  try {
    // First, make sure all referenced keyframes are in the cloud
    const refIds = (seq.steps || []).map(s => s.keyframeId).filter(Boolean);
    await upsertKeyframes(refIds);

    const payload = sequencePayload(seq);
    if (seq._cloudId) {
      const { error } = await supabase
        .from('animations')
        .update(payload)
        .eq('id', seq._cloudId);
      if (error) throw error;
    } else {
      const { data, error } = await supabase
        .from('animations')
        .insert({ ...payload, user_id: getUser().id, is_public: false })
        .select('id')
        .single();
      if (error) throw error;
      seq._cloudId = data.id;
      seq._isPublic = false;
    }
    seq._dirty = false;
    seq._lastSavedAt = Date.now();
    lastSavedSequenceJson.set(seq._cloudId, JSON.stringify(payload));
    setStatus('saved');
  } catch (e) {
    setStatus('error');
    throw e;
  }
}

export async function publishSequence(idx) {
  const seq = state.project.sequences[idx];
  if (!seq?._cloudId) throw new Error('Save to cloud before publishing');
  const { error } = await supabase
    .from('animations')
    .update({ is_public: true })
    .eq('id', seq._cloudId);
  if (error) throw error;
  seq._isPublic = true;
}

export async function unpublishSequence(idx) {
  const seq = state.project.sequences[idx];
  if (!seq?._cloudId) return;
  const { error } = await supabase
    .from('animations')
    .update({ is_public: false })
    .eq('id', seq._cloudId);
  if (error) throw error;
  seq._isPublic = false;
}

export async function deleteSequenceFromCloud(idx) {
  const seq = state.project.sequences[idx];
  if (!seq?._cloudId) return;
  const { error } = await supabase
    .from('animations')
    .delete()
    .eq('id', seq._cloudId);
  if (error) throw error;
  lastSavedSequenceJson.delete(seq._cloudId);
  delete seq._cloudId;
  delete seq._isPublic;
  delete seq._lastSavedAt;
  seq._dirty = true;
}

export async function deleteAnimationById(animationId) {
  if (!isAuthenticated()) return;
  const { error } = await supabase
    .from('animations')
    .delete()
    .eq('id', animationId);
  if (error) throw error;
  lastSavedSequenceJson.delete(animationId);
  for (const seq of state.project.sequences || []) {
    if (seq._cloudId === animationId) {
      delete seq._cloudId;
      delete seq._isPublic;
      delete seq._lastSavedAt;
      seq._dirty = true;
    }
  }
}

export async function setAnimationPublicById(animationId, isPublic) {
  if (!isAuthenticated()) return;
  const { error } = await supabase
    .from('animations')
    .update({ is_public: isPublic })
    .eq('id', animationId);
  if (error) throw error;
  for (const seq of state.project.sequences || []) {
    if (seq._cloudId === animationId) seq._isPublic = isPublic;
  }
}

export async function deleteKeyframeFromCloud(id) {
  if (!isAuthenticated()) return;
  const { error } = await supabase
    .from('keyframes')
    .delete()
    .eq('id', id);
  if (error) throw error;
  lastSavedKeyframeJson.delete(id);
  const kf = state.project.keyframes[id];
  if (kf) delete kf._syncedAt;
}

// ----------------------------------------------------------------
// Autosave tick — invoked via the state module's cloud save hook.
// ----------------------------------------------------------------
export function scheduleAutosave() {
  if (!isAuthenticated() || !isSupabaseConfigured()) return;
  clearTimeout(autosaveTimer);
  autosaveTimer = setTimeout(runAutosave, AUTOSAVE_DELAY);
}

async function runAutosave() {
  if (!isAuthenticated() || !isSupabaseConfigured()) return;
  try {
    // 1) upsert every keyframe in the user's library (diffed)
    const allKfIds = Object.keys(state.project.keyframes);
    let didWork = false;
    if (allKfIds.length) {
      const before = lastSavedKeyframeJson.size;
      await upsertKeyframes(allKfIds);
      if (lastSavedKeyframeJson.size !== before) didWork = true;
    }

    // 2) update cloud-linked sequences whose content changed
    for (const seq of state.project.sequences) {
      if (!seq._cloudId) continue;
      const payload = sequencePayload(seq);
      const snap = JSON.stringify(payload);
      if (lastSavedSequenceJson.get(seq._cloudId) === snap) continue;
      setStatus('saving');
      // ensure any newly referenced keyframes exist in cloud
      await upsertKeyframes(payload.keyframe_ids);
      const { error } = await supabase
        .from('animations')
        .update(payload)
        .eq('id', seq._cloudId);
      if (error) throw error;
      lastSavedSequenceJson.set(seq._cloudId, snap);
      seq._lastSavedAt = Date.now();
      seq._dirty = false;
      didWork = true;
    }
    if (didWork) setStatus('saved');
  } catch (e) {
    console.warn('Autosave failed:', e);
    setStatus('error');
  }
}

// ----------------------------------------------------------------
// Hydrate user library on sign-in
// ----------------------------------------------------------------
export async function hydrateUserLibrary() {
  if (!isAuthenticated() || !isSupabaseConfigured()) return;

  const { data, error } = await supabase.rpc('get_user_library');
  if (error) { console.warn('hydrate failed:', error); return; }
  if (!data) return;

  const cloudKeyframes = data.keyframes || [];
  const cloudAnimations = data.animations || [];

  // Merge keyframes: cloud rows win
  const now = Date.now();
  for (const row of cloudKeyframes) {
    state.project.keyframes[row.id] = {
      id: row.id,
      name: row.name,
      rows: row.rows,
      cols: row.cols,
      grid: row.grid,
      defaultColor: row.default_color || '#ca4b15',
      backgroundColor: null,
      _syncedAt: now,
    };
    lastSavedKeyframeJson.set(
      row.id,
      JSON.stringify(keyframePayload(state.project.keyframes[row.id]))
    );
  }

  // Merge animations by cloud id
  const byCloudId = new Map();
  for (const a of cloudAnimations) byCloudId.set(a.id, a);

  const existingCloudIds = new Set(
    (state.project.sequences || [])
      .map(s => s._cloudId)
      .filter(Boolean)
  );

  for (const seq of state.project.sequences) {
    if (seq._cloudId && byCloudId.has(seq._cloudId)) {
      const row = byCloudId.get(seq._cloudId);
      // Rehydrate fields from cloud row
      const sd = row.sequence_data || {};
      seq.name = sd.name ?? seq.name;
      seq.steps = sd.steps ?? seq.steps;
      seq.loop = sd.loop ?? seq.loop;
      seq._isPublic = row.is_public;
      seq._lastSavedAt = new Date(row.updated_at).getTime();
      seq._dirty = false;
      lastSavedSequenceJson.set(row.id, JSON.stringify(sequencePayload(seq)));
    }
  }

  // Append sequences that exist in cloud but not locally
  for (const row of cloudAnimations) {
    if (existingCloudIds.has(row.id)) continue;
    const sd = row.sequence_data || {};
    const seq = {
      name: sd.name || row.title || 'Untitled',
      steps: sd.steps || [],
      loop: sd.loop !== false,
      _cloudId: row.id,
      _isPublic: row.is_public,
      _lastSavedAt: new Date(row.updated_at).getTime(),
      _dirty: false,
    };
    state.project.sequences.push(seq);
    lastSavedSequenceJson.set(row.id, JSON.stringify(sequencePayload(seq)));
  }

  libraryChangedCb();
}

export function clearCloudStateOnSignOut() {
  lastSavedKeyframeJson.clear();
  lastSavedSequenceJson.clear();
  for (const seq of state.project?.sequences || []) {
    delete seq._cloudId;
    delete seq._isPublic;
    delete seq._lastSavedAt;
    delete seq._dirty;
  }
  for (const kf of Object.values(state.project?.keyframes || {})) {
    delete kf._syncedAt;
  }
  setStatus('idle');
  libraryChangedCb();
}

// ----------------------------------------------------------------
// Viewer / explore helpers
// ----------------------------------------------------------------
export async function loadAnimationWithKeyframes(animationId) {
  if (!isSupabaseConfigured()) return null;
  const { data, error } = await supabase.rpc('get_animation_with_keyframes', {
    p_id: animationId,
  });
  if (error) throw error;
  return data; // { animation, keyframes: [...] } or null
}

// Convert a {animation, keyframes[]} RPC payload into the legacy
// {project, sequence} shape that the viewer + explore preview consume.
export function rpcToLegacyProject(rpcData) {
  if (!rpcData) return null;
  const a = rpcData.animation;
  const kfArr = rpcData.keyframes || [];
  const keyframes = {};
  for (const k of kfArr) {
    keyframes[k.id] = {
      id: k.id,
      name: k.name,
      rows: k.rows,
      cols: k.cols,
      grid: k.grid,
      defaultColor: k.default_color || '#ca4b15',
    };
  }
  const sd = a.sequence_data || {};
  return {
    id: a.id,
    title: a.title,
    user_id: a.user_id,
    project: {
      version: 1,
      gridSize: sd.gridSize || { rows: 3, cols: 9 },
      backgroundColor: sd.backgroundColor || '#012b36',
      fontSize: sd.fontSize || 48,
      keyframes,
      sequences: [{
        name: sd.name || 'animation',
        steps: sd.steps || [],
        loop: sd.loop !== false,
      }],
    },
  };
}

// ----------------------------------------------------------------
// My Animations page: lightweight list
// ----------------------------------------------------------------
export async function listUserAnimations() {
  if (!isAuthenticated() || !isSupabaseConfigured()) return [];
  const { data, error } = await supabase
    .from('animations')
    .select('id, title, is_public, thumbnail_text, thumbnail_color, thumbnail_rows, thumbnail_cols, updated_at')
    .eq('user_id', getUser().id)
    .order('updated_at', { ascending: false });
  if (error) throw error;
  return data || [];
}
